import { useEffect, useMemo, useRef } from "react";

// Interfaces
import { ElrHroBonusVariantProps } from "./interfaces";
import { IElrHroContentData, TBonus, TOptions, TTierBonus } from "../interfaces";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { getTierLastIndex, resetTiersInputValues, setTiersLimits } from "@/Common/helpers/Matrices/tiers";
import { ComplexCommission, ComplexCommissionTier } from "@/PayPlans/helpers/DefaultSections";
import { isCommissionValid, isTierConditionsValid, validateElrHroTiers } from "../validations-fn";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierRows from "@/Layouts/components/Tiers/TierRows";

// Custom components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

// MUI
import { Collapse, Grid, Stack } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

import {
	getHeaderOptions,
	getInputOptionByType,
	getInputsOptions,
	getInputStepValue,
	getOptionIdentifierByVariantType,
	getSelectedOption,
	getTiersInputType,
	getTiersLabel,
	resetTiersOnInputsMismatch,
} from "../helpers";

// Constants
const minimumColumns = 1;
const maximumColumns = 3;
const minimumTiers = 1;
const maximumTiers = 100;

export default function ElrHroBonusVariant(props: Readonly<ElrHroBonusVariantProps>) {
	const {
		content,
		identifier = "",
		variant = "ELR",
		tooltips,
		isSomeConfigured = true,
		readOnly = false,
		relativeToStore = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props ?? {};

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrorsMessages = getErrorsMessages();

	const headerErrors = errors?.get("header");
	const rowsErrors = errors?.get("rows");
	const columnsErrors = errors?.get("bonuses");
	const minimumRangeError = Boolean(errors?.get("range")?.length);

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onModelUpdate = (updatedModel: IElrHroContentData) => {
		const isAllValid = isValid && isTierConditionsValid({ isSomeConfigured, tiers: updatedModel?.tiers ?? [] });
		onActionValidation?.(identifier, isAllValid);
		if (wasUpdated.current) {
			wasUpdated.current = false;
			onActionUpdate?.(identifier, updatedModel);
		}
	};

	// States
	const [innerModel, setInnerModel] = useCallbackState(null, onModelUpdate);
	const selectableOptions = getHeaderOptions(variant);

	const inputHeaderDefinitions = getInputsOptions(innerModel?.option ?? "gross", relativeToStore);
	const optionIdentifier = getOptionIdentifierByVariantType(variant);
	const inputTiersDefinitions = getInputsOptions(optionIdentifier, relativeToStore);

	// Control Last Bonus Error
	const lastBonusError = !isCommissionValid({ isSomeConfigured, tiers: innerModel?.tiers || [] });

	// Action handlers
	const handleUpdateOption = (selection: TOptions) => {
		wasUpdated.current = true;
		// Reset tiers headers
		const inputHeaderDefinitionsLocal = getInputsOptions(selection.value, relativeToStore);
		const inputType = inputHeaderDefinitionsLocal?.[0]?.type ?? "Percent";
		const params = { column: "bonuses" };

		let updatedTiers = resetTiersInputValues([...innerModel.tiers], {
			params,
			attributes: ["from", "to"],
			valueToSet: 0,
			inputToSet: inputType,
		});

		// Set tiers headers limits
		const guideOption = getInputOptionByType(inputHeaderDefinitionsLocal, inputType);
		const limits = { min: guideOption.min ?? 0, max: guideOption.max ?? 0 };
		updatedTiers = setTiersLimits(updatedTiers, { params, limits: { columns: limits }, setColumns: true });

		update({ ...innerModel, option: selection.value, tiers: updatedTiers });
	};

	const handleAddColumn = () => {
		const updatedTiers = [...innerModel.tiers];
		const inputType = getTiersInputType(updatedTiers?.[0]?.["bonuses"] ?? [], "from");
		const bonusInput = getTiersInputType(updatedTiers?.[0]?.["bonuses"] ?? [], "bonus");
		const guideOption = getInputOptionByType(inputHeaderDefinitions, inputType);
		const lastIndex = getTierLastIndex(updatedTiers, { column: "bonuses" });
		const maximumInput = guideOption?.max ?? 1000000;

		updatedTiers.forEach((tier) => {
			tier.bonuses.push(ComplexCommission([0, inputType], [maximumInput, inputType], { commission: [0, bonusInput] }));
			if (tier?.bonuses?.[lastIndex]) {
				tier.bonuses[lastIndex]["to"]["value"] = 0;
			}
		});

		wasUpdated.current = true;
		setInnerModel({ ...innerModel, tiers: updatedTiers });
	};

	const handleDeleteColumn = () => {
		const updatedTiers = [...innerModel.tiers];
		const inputType = getTiersInputType(updatedTiers?.[0]?.["bonuses"] ?? [], "from");
		const guideOption = getInputOptionByType(inputHeaderDefinitions, inputType);
		const maximumInput = guideOption?.max ?? 1000000;
		if (updatedTiers?.[0]?.["bonuses"]?.length > minimumColumns) {
			updatedTiers.forEach((tier) => {
				tier.bonuses.splice(-1);
				const lastIndex = getTierLastIndex(tier["bonuses"]);
				tier.bonuses[lastIndex]["to"]["value"] = maximumInput;
			});
		}
		update({ ...innerModel, tiers: updatedTiers });
	};

	const handleAddTier = () => {
		const updatedTiers = [...innerModel.tiers];
		const lastIndex = getTierLastIndex(updatedTiers);
		const lastColumnsIndex = getTierLastIndex(updatedTiers, { column: "bonuses" });

		const inputRows = getTiersInputType(updatedTiers, "from");
		const inputColumns = getTiersInputType(updatedTiers?.[0]?.["bonuses"] ?? [], "from");
		const inputBonus = getTiersInputType(updatedTiers?.[0]?.["bonuses"] ?? [], "bonus");

		const bonuses = [];
		for (let i = 0; i <= lastColumnsIndex; i++) {
			const previousFrom = updatedTiers?.[0]?.["bonuses"]?.[i]?.["from"]?.["value"] ?? 0;
			const previousTo = updatedTiers?.[0]?.["bonuses"]?.[i]?.["to"]?.["value"] ?? 0;
			bonuses[i] = ComplexCommission([previousFrom, inputColumns], [previousTo, inputColumns], { commission: [0, inputBonus] });
		}

		const guideOption = getInputOptionByType(inputTiersDefinitions, inputRows);
		const maximumInput = guideOption?.max ?? 1000000;
		updatedTiers.push(
			ComplexCommissionTier([0, inputRows], [maximumInput, inputRows], {
				commissions: bonuses,
				identifier: "bonuses",
			})
		);

		if (updatedTiers?.length) {
			updatedTiers[lastIndex].to.value = 0;
		}

		wasUpdated.current = true;
		setInnerModel({ ...innerModel, tiers: updatedTiers });
	};

	const handleDeleteTier = () => {
		const updatedTiers = [...innerModel.tiers];
		if (updatedTiers.length >= 1) {
			updatedTiers.splice(-1);
		}

		const lastIndex = getTierLastIndex(updatedTiers);
		const inputRows = getTiersInputType(updatedTiers, "from");
		const guideOption = getInputOptionByType(inputTiersDefinitions, inputRows);
		const maximumInput = guideOption?.max ?? 1000000;
		updatedTiers[lastIndex].to.value = maximumInput;

		update({ ...innerModel, tiers: updatedTiers });
	};

	const handleUpdateValue = (row: number, column: number, location: string, area: string, value: unknown) => {
		const updatedTiers = [...innerModel.tiers];
		const updatedValue = value ? Number(value) : 0;

		const inputRows = getTiersInputType(updatedTiers, "from");
		const inputColumns = getTiersInputType(updatedTiers?.[0]?.["bonuses"] ?? [], "from");
		const stepRowValue = getInputStepValue(inputRows);
		const stepColumnValue = getInputStepValue(inputColumns);

		switch (location) {
			case "column":
				updatedTiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus: TBonus, key: number) => {
						if (area in bonus && key === column) {
							bonus[area]["value"] = updatedValue;
							if (tier.bonuses?.[key + 1]) {
								tier.bonuses[key + 1]["from"]["value"] = (updatedValue + stepColumnValue).toFixed(2);
							}
						}
					});
				});
				break;
			case "row":
				if (area !== "bonus") {
					updatedTiers[row][area]["value"] = updatedValue;
					if (updatedTiers?.[row + 1] && area === "to") {
						updatedTiers[row + 1]["from"]["value"] = (updatedValue + stepRowValue).toFixed(2);
					}
				} else {
					updatedTiers[row]["bonuses"][column][area]["value"] = updatedValue;
				}
				break;
			default:
				break;
		}

		update({ ...innerModel, tiers: updatedTiers });
	};

	const handleUpdateInput = (_row: number, _column: number, location: string, area: string, input: string) => {
		let updatedTiers = [...innerModel.tiers];
		let guideOption: ReturnType<typeof getInputOptionByType>;
		let limits: { min: number; max: number };
		switch (location) {
			case "column":
				// // Set tiers headers inputs
				updatedTiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus: TBonus) => {
						bonus["from"]["input"] = input;
						bonus["from"]["value"] = 0;
						bonus["to"]["input"] = input;
						bonus["to"]["value"] = 0;
					});
				});

				// Set tiers headers limits
				guideOption = getInputOptionByType(inputHeaderDefinitions, input);
				limits = { min: guideOption.min ?? 0, max: guideOption.max ?? 0 };
				updatedTiers = setTiersLimits(updatedTiers, {
					params: { column: "bonuses" },
					limits: { columns: limits },
					setColumns: true,
				});
				break;
			case "row":
				if (area === "bonus") {
					// Set bonus inputs
					updatedTiers.forEach((tier) => {
						tier.bonuses?.forEach((bonus: TBonus) => {
							if (area in bonus) {
								bonus[area]["input"] = input;
								bonus[area]["value"] = 0;
							}
						});
					});
				} else {
					// Set tiers inputs
					updatedTiers.forEach((tier) => {
						tier["from"]["input"] = input;
						tier["from"]["value"] = 0;
						tier["to"]["input"] = input;
						tier["to"]["value"] = 0;
					});

					// Set tiers limits
					guideOption = getInputOptionByType(inputTiersDefinitions, input);
					limits = { min: guideOption.min ?? 0, max: guideOption.max ?? 0 };
					updatedTiers = setTiersLimits(updatedTiers, { limits: { rows: limits }, setRows: true });
				}
				break;
			default:
				break;
		}
		update({ ...innerModel, tiers: updatedTiers });
	};

	const update = (updatedModel: IElrHroContentData) => {
		wasUpdated.current = true;
		setInnerModel(updatedModel);
	};

	useMemo(() => {
		const innerTiers = innerModel?.tiers ?? [];
		if (innerTiers?.length) {
			const validationMethod = validateElrHroTiers(innerTiers, innerModel?.option, isSomeConfigured);
			validate(validationMethod);
		}
	}, [innerModel, isSomeConfigured, validate]);

	useMemo(() => {
		// RELATIVE TO STORE SWITCH LOGIC
		if (innerModel && !relativeToStore) {
			const { tiers: currentTiers, mismatch } = resetTiersOnInputsMismatch(innerModel?.tiers, variant, innerModel?.option);
			if (mismatch) {
				setInnerModel({ ...innerModel, tiers: currentTiers });
				wasUpdated.current = true;
			}
		}
	}, [relativeToStore, innerModel]);

	useEffect(() => {
		setInnerModel(clone(content));
		return () => {
			wasUpdated.current = false;
			setInnerModel(null);
		};
	}, [content, relativeToStore, setInnerModel]);

	return (
		<Grid container item mt={2} xs={12}>
			<Grid item xs={12}>
				<Stack position={"absolute"} direction={"row"}>
					<SmartSelector
						selectBy={"value"}
						showBy={"label"}
						options={selectableOptions}
						selected={getSelectedOption(innerModel?.option)}
						disabled={readOnly}
						sx={{ width: "110px", marginLeft: "85px" }}
						onChangeAction={(selection) => handleUpdateOption(selection as TOptions)}
					/>
				</Stack>
				<TierColumns
					showTitle
					titleLabel={""}
					titleContainerProps={{
						justifyContent: "flex-start",
						alignItems: "end",
						sx: { maxWidth: "241px", width: "241px", marginRight: "33px", marginLeft: "-29px" },
					}}
					titleProps={{
						sx: { marginTop: "3px", marginRight: "0px" },
						icon: {
							present: Boolean(tooltips?.headerTooltip),
							text: tooltips?.headerTooltip ?? "",
						},
					}}
					disabledDelete={innerModel?.tiers?.[0]?.bonuses?.length <= minimumColumns || readOnly}
					disabledAdd={innerModel?.tiers?.[0]?.bonuses?.length >= maximumColumns || readOnly}
					onClickDelete={handleDeleteColumn}
					onClickAdd={handleAddColumn}
				>
					<Stack direction={"row"} position={"relative"}>
						{innerModel?.tiers?.[0]?.bonuses?.map((tier: TTierBonus, key: number) => {
							const fromDisabled = Boolean(!key);
							const toDisabled = Boolean(innerModel?.tiers?.[0]?.bonuses.length === key + 1);
							const columnItemKey = `tier_column_${key}`;

							return (
								<TierColumnsItem key={columnItemKey} index={key} title={"Bonus"}>
									<MatrixColumn
										index={key}
										item={tier}
										errors={headerErrors || []}
										inputDefinitions={inputHeaderDefinitions}
										fromDisabled={fromDisabled}
										toDisabled={toDisabled}
										readOnly={readOnly}
										onActionUpdate={handleUpdateValue}
										onChangeType={handleUpdateInput}
									/>
								</TierColumnsItem>
							);
						})}
					</Stack>
				</TierColumns>
			</Grid>

			<Grid item xs={12} mt={2} minHeight={"135px"}>
				<TierRows
					showTitle
					title={getTiersLabel(variant)}
					sxTitlePropsContainer={{
						top: 102,
						left: 16,
					}}
					centerTitleAfterRow={2}
					titleProps={{
						className: "tiered-title",
						sx: { transform: "rotate(270deg)" },
						icon: {
							present: Boolean(tooltips?.tiersTooltip),
							text: tooltips?.tiersTooltip ?? "",
						},
					}}
					deleteButtonText={"Remove Tier"}
					addButtonText={"Add Tier"}
					deleteButtonDisabled={innerModel?.tiers?.length <= minimumTiers || readOnly}
					addButtonDisabled={innerModel?.tiers?.length >= maximumTiers || readOnly}
					spaces={{
						mainSection: 235,
						columns: 210,
						totalColumns: innerModel?.tiers?.[0]?.bonuses?.length ?? 1,
						totalRows: innerModel?.tiers?.length ?? 1,
						separation: 20,
					}}
					rowsAreaProps={{
						ml: "35px",
					}}
					onClickDelete={handleDeleteTier}
					onClickAdd={handleAddTier}
				>
					<TransitionGroup>
						<Collapse>
							<Stack direction="column" rowGap={2}>
								{innerModel?.tiers?.map((tier: TTierBonus, key: number) => {
									const rowKey = `market_share_row_${key}`;
									const fromDisabled = Boolean(!key);
									const toDisabled = Boolean(innerModel?.tiers?.length === key + 1);
									const bonusesLength = innerModel?.tiers?.[0]?.bonuses?.length || 0;
									const commissionsLength = innerModel?.tiers?.[0]?.bonuses?.length || 0;
									const reduction = commissionsLength * 6.25 + 52 + commissionsLength;

									const getColumnsErrors = (nativeErrors = [], complainErrors = false) => {
										if (nativeErrors?.length) {
											return nativeErrors;
										}
										return [[{ bonus: complainErrors }]];
									};

									return (
										<Grid key={rowKey} container item>
											<MatrixRow
												index={key}
												item={tier}
												fromDisabled={fromDisabled}
												toDisabled={toDisabled}
												inputDefinitions={inputTiersDefinitions}
												spaces={{
													reduction,
													mainSection: 235,
													columns: 210,
													totalColumns: bonusesLength ?? 1,
													totalRows: innerModel?.tiers?.length ?? 1,
													separation: 8.5,
												}}
												rowsErrors={rowsErrors || []}
												columnsErrors={getColumnsErrors(columnsErrors, minimumRangeError)}
												lastBonusError={lastBonusError}
												readOnly={readOnly}
												spacing={"10.5px"}
												sxMainContent={{ minWidth: "235px" }}
												sxCells={{ minWidth: "210px" }}
												onActionUpdate={handleUpdateValue}
												onChangeType={handleUpdateInput}
											/>
										</Grid>
									);
								})}
							</Stack>
						</Collapse>
					</TransitionGroup>

					{Boolean(localErrorsMessages?.length || lastBonusError) && (
						<Grid container direction="column" rowSpacing={0.2} mt={1} alignItems="flex-start">
							{localErrorsMessages.map((message, key) => {
								const errorKey = `error_market_share_${message}_${key}`;
								return <TierAlertError key={errorKey} text={message} />;
							})}
							{lastBonusError && <TierAlertError text={"Last commission must be configured"} />}
						</Grid>
					)}
				</TierRows>
			</Grid>
		</Grid>
	);
}
